import { useEffect, useRef, useCallback } from 'react';
import { gsap } from 'gsap';
import { GridBackgroundProps } from '../../types';

const GridBackground = ({ children, shootingStarCount = 5, className = '' }: GridBackgroundProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const glowRef = useRef<HTMLDivElement>(null);
  const starsContainerRef = useRef<HTMLDivElement>(null);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    const glow = glowRef.current;
    const container = containerRef.current;
    if (!glow || !container) return;

    const rect = container.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    glow.style.setProperty('--glow-x', `${x}px`);
    glow.style.setProperty('--glow-y', `${y}px`);
    glow.style.opacity = '1';
  }, []);

  const handleMouseLeave = useCallback(() => {
    const glow = glowRef.current;
    if (!glow) return;
    glow.style.opacity = '0';
  }, []);

  useEffect(() => {
    const container = starsContainerRef.current;
    if (!container) return;

    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return;

    const stars: HTMLDivElement[] = [];
    const animations: gsap.core.Tween[] = [];

    for (let i = 0; i < shootingStarCount; i++) {
      const star = document.createElement('div');
      container.appendChild(star);
      stars.push(star);
      animateStar(star, animations);
    }

    function animateStar(star: HTMLDivElement, anims: gsap.core.Tween[]) {
      const isHorizontal = Math.random() > 0.3;

      star.className = isHorizontal
        ? 'grid-background__star grid-background__star--horizontal'
        : 'grid-background__star grid-background__star--vertical';

      const width = isHorizontal ? gsap.utils.random(150, 300) : gsap.utils.random(1, 1.5);
      const height = isHorizontal ? gsap.utils.random(0.5, 1) : gsap.utils.random(150, 300);

      const startX = isHorizontal
        ? gsap.utils.random(-400, window.innerWidth * 0.4)
        : gsap.utils.random(0, window.innerWidth);
      const startY = isHorizontal
        ? gsap.utils.random(0, window.innerHeight)
        : gsap.utils.random(-400, window.innerHeight * 0.3);

      const moveX = isHorizontal ? gsap.utils.random(800, 1600) : 0;
      const moveY = isHorizontal ? 0 : gsap.utils.random(800, 1600);

      gsap.set(star, {
        width,
        height,
        x: startX,
        y: startY,
        opacity: 0,
        rotation: isHorizontal ? gsap.utils.random(-1, 1) : 0,
      });

      const duration = gsap.utils.random(6, 14);
      const peakOpacity = gsap.utils.random(0.25, 0.6);

      const tween = gsap.to(star, {
        x: startX + moveX,
        y: startY + moveY,
        duration,
        delay: gsap.utils.random(0, 10),
        ease: 'none',
        onStart: () => {
          gsap.to(star, {
            opacity: peakOpacity,
            duration: duration * 0.15,
            ease: 'power2.in',
          });
          gsap.to(star, {
            opacity: 0,
            duration: duration * 0.3,
            delay: duration * 0.65,
            ease: 'power2.out',
          });
        },
        onComplete: () => {
          animateStar(star, anims);
        },
      });

      anims.push(tween);
    }

    return () => {
      animations.forEach((a) => a.kill());
      stars.forEach((s) => s.remove());
    };
  }, [shootingStarCount]);

  return (
    <div
      className={`grid-background${className ? ` ${className}` : ''}`}
      ref={containerRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <div className="grid-background__grid" aria-hidden="true" />
      <div className="grid-background__glow" ref={glowRef} aria-hidden="true" />
      <div className="grid-background__stars" ref={starsContainerRef} aria-hidden="true" />
      <div className="grid-background__content">{children}</div>
    </div>
  );
};

export default GridBackground;
